/**
 * ncui dropdown을 사용
 * 필터 내부에서 직접 searchParams를 변경하지 않음
 * 필터를 사용하는 컴포넌트에서 searchParams 변경 로직 추가하기
 * 아이온-캐릭터, L2-게시판 등 searchParams를 한번에 제어해야할 때 사용함
 */

import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import { PLATFORM } from '@constants/index';
import { getPlatform } from '@utils/platform';
import type { l10nType, filterType, l10nFilterType } from '@type/index';

interface IFilterProps {
  filterData: filterType;
  option: any;
  searchParams: URLSearchParams;
  changeCallback?: () => void;
  setDropdownMap?: (dropdownMap: { [key: string]: any }) => void;
}

const defaultOption = {
  // dropdown 옵션
  parent: null,
  name: 'filter1', // filter1 ~ 4
  data: [], // [{title, value}] or [{root}] (allCategories API)
  isInitialActive: false, // 초기 로드 시 첫번째 요소를 활성화할지 여부
  isInitialCallbackCall: false, // 초기 실행 시 콜백 실행 여부
  useSelectExceptPc: true, // PC가 아닐 때, dropdown을 select로 사용할지 여부

  // 아이온 별도 처리를 위한 옵션
  useAllBlankValue: false, // "전체"(빈값)을 사용하는지 여부(dropdown옵션은 아니지만 isInitialActive 제어를 위해 각 필터에서 받음)
  initialActiveValue: null, // dropdown 초기 로드 시 활성화하고자 하는 값
};

export const setl10nKeyToTitle = (filterData: l10nFilterType, l10n: l10nType): filterType => {
  if (!Array.isArray(filterData) || filterData.length === 0) {
    return [];
  }

  return filterData.map((v) => ({ title: v?.l10nKey ? l10n.filter[v?.l10nKey] : v?.title || '', value: v?.value }));
};

const FilterOnlyUi = ({ filterData, option, searchParams, changeCallback, setDropdownMap }: IFilterProps) => {
  if (!window?.ncui?.dropdown || !Array.isArray(filterData) || filterData?.length === 0) {
    return <Fragment />;
  }

  // 외부 라이브러리 함수 내부에서 state 갱신이 되지 않아, 외부에서 useEffect로 감지하도록 구현
  const [activeData, setActiveData] = useState<{ d1: string; d2?: string } | null>(null);
  const filterRef = useRef(null);
  let dropdownLayer;

  // 초기 로드 시 필터 활성화
  const setInitialActiveValue = useCallback(
    (dropdownLayer, option) => {
      if (!dropdownLayer) {
        return;
      }

      let activeValue = option.initialActiveValue || '';

      if (!activeValue && searchParams.get(option.name)) {
        activeValue = searchParams.get(option.name);
      }

      if (activeValue || activeValue === '') {
        dropdownLayer.activeValue([activeValue]);
      }
    },
    [searchParams]
  );

  // 필터 셋팅
  const setFilter = useCallback(() => {
    const options = { ...defaultOption, ...option, data: filterData };
    const hasFilterValueInParam = !!searchParams.get(options?.name);

    if (filterRef?.current) {
      options.parent = filterRef.current;
    }

    // 퍼플앱이면 useSelectExceptPc: false (모바일에서도 pc로 노출됨)
    if (getPlatform() === PLATFORM.PURPLE.APP) {
      options.useSelectExceptPc = false;
    }

    // 콜백 추가
    if (!options?.changeCallback) {
      options.changeCallback = (activeData: { d1: string; d2?: string }) => {
        setActiveData(activeData);
        typeof changeCallback === 'function' && changeCallback(option.name, activeData);
      };
    }

    // "전체" 사용하지 않을 때, param에 필터값이 없으면 첫번째 요소 활성화 사용함 ()
    if (!options.useAllBlankValue) {
      options.isInitialActive = !hasFilterValueInParam;
    }

    dropdownLayer = new window.ncui.dropdown(options);
    setDropdownMap && setDropdownMap({ [option.name]: dropdownLayer });

    if (options.useAllBlankValue || (!options.useAllBlankValue && hasFilterValueInParam)) {
      setInitialActiveValue(dropdownLayer, options);
    }
  }, [filterData]);

  useEffect(() => {
    setFilter();

    // unmount
    return () => {
      dropdownLayer = null;
    };
  }, []);

  return <div className="search-filter" ref={filterRef}></div>;
};

export default FilterOnlyUi;
